/**
 * Admin broadcast panel — post messages that appear as a dismissible
 * banner on every user's portal until they mark it read. Kept minimal:
 * one message + kind (info / sale / urgent), no scheduling, no email.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Loader2, Trash2, Eye, EyeOff, Info, Tag, AlertTriangle } from "lucide-react";

const KIND_META = {
  info:   { icon: Info,          label: "Info",   color: "text-sky-400 border-sky-700 bg-sky-950/30" },
  sale:   { icon: Tag,           label: "Sale",   color: "text-amber-400 border-amber-700 bg-amber-950/30" },
  urgent: { icon: AlertTriangle, label: "Urgent", color: "text-rose-400 border-rose-700 bg-rose-950/30" },
};

export default function AdminBroadcast() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [kind, setKind] = useState("info");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/broadcasts").then((r) => setRows(r.data.broadcasts || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!msg.trim()) { toast.error("Type a message first"); return; }
    setBusy(true);
    try {
      await api.post("/broadcasts", { message: msg.trim(), kind });
      toast.success("Broadcast sent");
      setMsg("");
      load();
    } catch (e) {
      toast.error("Send failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (b) => {
    try {
      await api.patch(`/broadcasts/${b.id}`, { active: !b.active });
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete this broadcast? (${b.message.slice(0, 60)}…)`)) return;
    try {
      await api.delete(`/broadcasts/${b.id}`);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="border-l-4 border-orange-600 pl-5 mb-8">
        <div className="label-tiny mb-2 inline-flex items-center gap-2">
          <Megaphone size={12} /> Admin
        </div>
        <h1 className="font-display uppercase text-4xl font-bold leading-[0.95]">Broadcasts</h1>
        <p className="text-stone-400 mt-3 text-sm">
          Send a message to every user in the platform. Shows up as a dismissible banner on their portal.
        </p>
      </div>

      {/* Compose */}
      <div className="border border-stone-800 p-5 mb-8 bg-stone-950" data-testid="broadcast-compose">
        <div className="font-display uppercase text-sm tracking-wider text-orange-400 mb-3">New Broadcast</div>
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          placeholder="e.g. New photos from the Fall Classic are now available — check the Buy a Photo tab!"
          maxLength={500}
          className="field-input w-full min-h-[90px] mb-3"
          data-testid="broadcast-message"
        />
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {Object.entries(KIND_META).map(([k, m]) => {
              const Icon = m.icon;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`btn-secondary !py-1 text-xs inline-flex items-center gap-1 ${kind === k ? m.color : ""}`}
                  data-testid={`broadcast-kind-${k}`}
                >
                  <Icon size={11} /> {m.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={send}
            disabled={busy || !msg.trim()}
            className="ml-auto btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="broadcast-send"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Megaphone size={13} />}
            Send to all users
          </button>
        </div>
        <div className="text-[10px] text-stone-500 mt-2 font-mono-tc">{msg.length}/500</div>
      </div>

      {/* History */}
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-display uppercase text-xl font-bold tracking-tight">History</h2>
        <span className="text-[10px] uppercase tracking-widest text-stone-500">{rows.length}</span>
      </div>
      {loading ? (
        <div className="text-stone-500 text-sm inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-10 text-center text-stone-500 text-sm">
          No broadcasts yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="broadcast-history">
          {rows.map((b) => {
            const meta = KIND_META[b.kind] || KIND_META.info;
            const Icon = meta.icon;
            return (
              <div key={b.id} className={`border p-3 flex items-start gap-3 ${b.active ? "border-stone-800" : "border-stone-800 opacity-50"}`}>
                <div className={`w-7 h-7 flex items-center justify-center border ${meta.color}`}>
                  <Icon size={12} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm break-words">{b.message}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1 font-mono-tc">
                    Sent by {b.sent_by_name} · {(b.created_at || "").slice(0, 16).replace("T", " ")} · Read by {b.read_count}
                  </div>
                </div>
                <button onClick={() => toggle(b)} className="text-stone-500 hover:text-sky-400" title={b.active ? "Hide from users" : "Show again"} data-testid={`broadcast-toggle-${b.id}`}>
                  {b.active ? <Eye size={13} /> : <EyeOff size={13} />}
                </button>
                <button onClick={() => remove(b)} className="text-stone-500 hover:text-rose-400" title="Delete" data-testid={`broadcast-del-${b.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
